import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAM_OPTIONS = [
  "Speed & Agility",
  "Strength & Conditioning",
  "Nutrition & Healthy Habits",
  "Community Experiences",
];

export const Registration = () => {
  const [form, setForm] = useState({
    parent_name: "",
    email: "",
    phone: "",
    child_name: "",
    child_age: "",
    program: PROGRAM_OPTIONS[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/registrations`, {
        ...form,
        child_age: parseInt(form.child_age, 10),
      });
      setDone(true);
      toast.success("Registration submitted! We'll reach out soon.");
      setForm({
        parent_name: "",
        email: "",
        phone: "",
        child_name: "",
        child_age: "",
        program: PROGRAM_OPTIONS[0],
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please check the form.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="register" data-testid="registration-section" className="relative py-24 md:py-32 border-t border-white/10 bg-[#070707]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.4em] text-[#C4F542] mb-6">/ 04 — Join The Hovse</p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-none mb-6">
              REGISTER<br />YOUR <span className="text-[#C4F542]">YOUTH.</span>
            </h2>
            <p className="text-base text-zinc-300 font-body leading-relaxed mb-5">
              Our <span className="text-[#C4F542] font-bold">HOVSE Foundation's FREE Summer Fitness Camp</span> is open to youth ages 10–18 and takes place every Sunday at 2:00 PM, now through August.
            </p>
            <p className="text-base text-zinc-400 font-body leading-relaxed mb-8">
              Led by <span className="text-white font-semibold">Master Trainer Philly Weeden</span>, this program helps youth build confidence, improve fitness, develop leadership skills, and stay active in a safe, encouraging environment.
            </p>
            <p className="text-sm text-zinc-400 font-body leading-relaxed mb-8">
              Registration is required. To attend, complete the registration form prior to the camp session and your name will be on our list at check in.
            </p>

            <div className="border border-white/10 bg-[#111111] p-6 mb-6">
              <p className="text-xs uppercase tracking-[0.35em] text-[#C4F542] mb-4">Camp Details</p>
              <ul className="space-y-3 font-body text-sm text-zinc-200">
                <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-[#C4F542]" /> FREE to attend</li>
                <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-[#C4F542]" /> Ages 10–18</li>
                <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-[#C4F542]" /> Sundays at 2:00 PM</li>
                <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-[#C4F542]" /> Now through August</li>
              </ul>
            </div>

            <p className="text-sm text-zinc-400 font-body">
              Click <span className="text-[#C4F542] font-bold">"Reserve Your Spot"</span> to pre-register today!
            </p>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div data-testid="registration-success" className="bg-[#111111] border border-[#C4F542]/40 p-10 lg:p-12 flex flex-col items-center text-center">
                <CheckCircle2 size={56} className="text-[#C4F542] mb-6" />
                <h3 className="font-display text-4xl text-white mb-3">YOU'RE IN.</h3>
                <p className="text-zinc-400 font-body max-w-md">
                  We got your registration. A coach will reach out within 48 hours with next steps.
                </p>
                <button
                  data-testid="register-another-btn"
                  onClick={() => setDone(false)}
                  className="mt-8 border border-white/30 text-white text-xs uppercase tracking-widest py-3 px-6 hover:border-[#C4F542] hover:text-[#C4F542] transition-colors"
                >
                  Register another kid
                </button>
              </div>
            ) : (
              <form
                data-testid="registration-form"
                onSubmit={onSubmit}
                className="bg-[#111111] border border-white/10 p-6 md:p-10 space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Parent / Guardian Name"
                    name="parent_name"
                    value={form.parent_name}
                    onChange={onChange}
                    testId="reg-parent-name"
                    required
                  />
                  <Field
                    label="Email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    testId="reg-email"
                    required
                  />
                  <Field
                    label="Phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    testId="reg-phone"
                    required
                  />
                  <Field
                    label="Child's Name"
                    name="child_name"
                    value={form.child_name}
                    onChange={onChange}
                    testId="reg-child-name"
                    required
                  />
                  <Field
                    label="Child's Age"
                    type="number"
                    name="child_age"
                    value={form.child_age}
                    onChange={onChange}
                    testId="reg-child-age"
                    min={3}
                    max={18}
                    required
                  />
                  <div className="flex flex-col gap-2">
                    <label className="text-xs uppercase tracking-[0.25em] text-zinc-400">Program</label>
                    <select
                      data-testid="reg-program"
                      name="program"
                      value={form.program}
                      onChange={onChange}
                      className="bg-[#1A1A1A] border border-white/15 text-white px-4 py-3 focus:outline-none focus:border-[#C4F542] font-body"
                    >
                      {PROGRAM_OPTIONS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs uppercase tracking-[0.25em] text-zinc-400">Anything we should know? (optional)</label>
                  <textarea
                    data-testid="reg-message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    className="bg-[#1A1A1A] border border-white/15 text-white px-4 py-3 focus:outline-none focus:border-[#C4F542] font-body resize-none"
                    placeholder="Medical notes, sports they love, schedule needs..."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="register-submit-btn"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#C4F542] text-black font-bold uppercase tracking-widest text-sm py-5 px-8 hover:bg-white transition-colors duration-300 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Submitting...
                    </>
                  ) : (
                    "Reserve Your Spot"
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testId, ...rest }) => (
  <div className="flex flex-col gap-2">
    <label className="text-xs uppercase tracking-[0.25em] text-zinc-400">{label}</label>
    <input
      data-testid={testId}
      {...rest}
      className="bg-[#1A1A1A] border border-white/15 text-white px-4 py-3 focus:outline-none focus:border-[#C4F542] font-body"
    />
  </div>
);
